// UI strings for EN / FI / SV. English is the source; Finnish and Swedish were
// drafted for the pilot and must be reviewed by a native speaker before
// go-live (see DESIGN_GUIDELINES.md §2). Keep strings short and idiom-free.
//
// Server-enforced lists (topic values, mood values) live in lib/constants.ts;
// only their display labels are here.

import { platformEn } from './platformStrings';
import { platformFi } from './platformFi';
import { platformSv } from './platformSv';
import { headEn, headFi, headSv } from './headStrings';
import { teacherEn, teacherFi, teacherSv } from './teacherStrings';
import { r2En, r2Fi, r2Sv } from './r2Strings';
import { r3En, r3Fi, r3Sv } from './r3Strings';
import { r4En, r4Fi, r4Sv } from './r4Strings';
import { r4bEn, r4bFi, r4bSv } from './r4bStrings';
import { r4cEn, r4cFi, r4cSv } from './r4cStrings';
import { r5En, r5Fi, r5Sv } from './r5Strings';
import { r6En, r6Fi, r6Sv } from './r6Strings';
import { r7En, r7Fi, r7Sv } from './r7Strings';

const en = {
  // shell / common
  'lang.label': 'Language',
  'skip': 'Skip to main content',
  'loading': 'Loading…',
  'signOut': 'Sign out',
  'a11y.link': 'Accessibility statement',
  'week': (week: number, year: number) => `Week ${week} · ${year}`,
  'outOf5': 'out of 5',
  'notFound.title': 'Page not found',
  'notFound.body': 'Check the link, or ask your teacher for today’s check-in link.',
  'role.teacher': 'Teacher',
  'role.schoolLead': 'School lead',
  'role.admin': 'Admin',
  'band.crit': 'Attention',
  'band.warn': 'Watch',
  'band.good': 'Healthy',

  // mood + topics
  'mood.1': 'Struggling',
  'mood.2': 'Not great',
  'mood.3': 'Okay',
  'mood.4': 'Good',
  'mood.5': 'Great',
  'topic.schoolwork': 'Schoolwork',
  'topic.friends': 'Friends',
  'topic.family': 'Family',
  'topic.sleep': 'Sleep',
  'topic.health': 'Health',
  'topic.exams': 'Exams',
  'topic.other': 'Something else',

  // student check-in
  'ci.pageTitle': 'Check-in',
  'ci.title': 'How are you doing today?',
  'ci.anon': 'Anonymous — your teacher only sees totals for the whole class.',
  'ci.scaleHint': '1 is the lowest, 5 is the highest.',
  'ci.topicsTitle': 'Anything on your mind?',
  'ci.topicsHint': 'Optional — pick as many as you like.',
  'ci.done': 'Done',
  'ci.sending': 'Sending…',
  'ci.pickFirst': 'Choose a face from 1 to 5 to continue.',
  'ci.error': 'That didn’t go through. Ask your teacher if the check-in is still open.',
  'ci.closedTitle': 'This check-in isn’t open right now',
  'ci.closedBody': 'Ask your teacher for today’s link or QR code.',
  'ci.doneTitle': 'Thanks — you’re checked in',
  'ci.doneBody': 'Nothing more to do. You can close this page.',
  'safety.label': 'Support information',
  // Placeholder: fill in the real counsellor name/room before go-live (design doc §4.6).
  'safety.message':
    'If you want to talk to someone, the school counsellor, the school nurse or any teacher can help. In an emergency, call 112. This tool shows teachers patterns, not individual answers, so it can’t send anyone to check on you.',

  // staff sign-in
  'login.pageTitle': 'Staff sign in',
  'login.title': 'Staff sign in',
  'login.codeIntro': 'Enter your school’s code to get started.',
  'login.codeLabel': 'School code',
  'login.codePlaceholder': 'e.g. riverside-high',
  'login.continue': 'Continue',
  'login.checking': 'Checking…',
  'login.codeError': 'We couldn’t find a school with that code. Check it with your school admin.',
  'login.codeHelp':
    'Don’t know your school’s code? Ask your school admin — it’s the code they were given when your school was set up.',
  'login.adminSkip': 'Signing in as the pilot admin? Skip this step.',
  'login.credIntro': 'Sign in with the account issued to you.',
  'login.signingInTo': 'Signing in to',
  'login.notYourSchool': 'Not your school?',
  'login.adminBanner': 'Pilot admin sign-in',
  'login.enterCodeInstead': 'Enter a school code instead',
  'login.email': 'Email',
  'login.password': 'Password',
  'login.signIn': 'Sign in',
  'login.signingIn': 'Signing in…',
  'login.credError': 'Email or password not recognized.',
  'login.wrongSchool': (s: string) =>
    `That account isn’t linked to ${s}. Double-check your school code, or sign in with the account issued for ${s}.`,

  // teacher
  'teacher.title': 'Your classes',
  'stat.classes': 'Classes',
  'stat.classesSub': 'you run check-ins for',
  'stat.open': 'Check-ins open now',
  'stat.openSub': 'each stays open 55 minutes',
  'addClass.title': 'Add a class',
  'addClass.label': 'Class label',
  'addClass.placeholder': 'e.g. Year 8 Form Room',
  'addClass.button': '+ Add class',
  'addClass.adding': 'Adding…',
  'addClass.error': 'Could not add that class — try again.',
  'addClass.added': (l: string) => `Class ${l} added.`,
  'teacher.empty': 'No classes yet — add one above, or ask your pilot coordinator to set one up.',
  'class.open': 'Check-in open',
  'class.closed': 'Closed',
  'session.share': (time: string) => `Share this code and link with the class. Open until ${time}.`,
  'session.codeLabel': 'Check-in code',
  'session.opened': (cls: string, code: string) => `Check-in opened for ${cls}. Code ${code}.`,
  'results.intro': 'Results show as class totals only, once enough students have answered.',
  'results.responses': (n: number) => `${n} responses`,
  'results.mood': 'How they’re doing',
  'results.topics': 'On their minds',
  'results.hidden': 'hidden',
  'results.hiddenNote': '“hidden” = too few students picked it to show safely.',
  'results.updated': (cls: string) => `Results updated for ${cls}.`,
  'results.average': 'Class average',
  'sup.insufficientTitle': 'Insufficient data to display safely',
  'sup.insufficientClass': (n: number, f: number) =>
    `Not enough responses yet (${n} of ${f} needed) to show a breakdown.`,
  'sup.unanimousTitle': 'Breakdown hidden',
  'sup.unanimousClass': (n: number) =>
    `${n} responses, but almost everyone answered the same way, which could expose the one person who didn’t.`,
  'btn.startCheckin': 'Start check-in',
  'btn.load': 'Load results',
  'btn.refresh': 'Refresh results',

  // school lead
  'school.title': 'School-wide results',
  'school.nav': 'School-wide',
  'stat.responses': 'Responses',
  'stat.responsesSub': 'across all participating classes',
  'stat.avg': 'School average',
  'stat.notLoaded': 'not loaded yet',
  'stat.insufficient': 'insufficient data',
  'stat.shown': 'What’s shown',
  'stat.shownValue': 'Totals',
  'stat.shownSub': 'aggregate, never individual',
  'school.panel': 'How students are doing',
  'school.intro':
    'Press “Load results” to pool every class’s check-ins, including classes too small to show on their own.',
  'school.refresh': 'Refresh',
  'school.updated': 'School-wide results updated.',
  'sup.insufficientSchool': (n: number, f: number) =>
    `Not enough responses across the school yet (${n} of ${f}).`,
  'sup.unanimousSchool': (n: number) =>
    `${n} responses, but the breakdown is hidden for the same reason a near-unanimous class is hidden.`,

  // admin
  'admin.title': 'Admin',
  'admin.navSchools': 'Schools',
  'admin.navAccounts': 'Accounts',
  'stat.schools': 'Schools',
  'stat.schoolsSub': 'on this platform',
  'stat.staff': 'Staff accounts',
  'stat.staffSubFiltered': 'in the filtered school',
  'stat.staffSub': 'teachers and school leads',
  'stat.respVisible': 'Responses visible here',
  'stat.respVisibleValue': 'None',
  'stat.respVisibleSub': 'admin is provisioning only',
  'admin.schools': 'Schools',
  'admin.schoolName': 'School name',
  'admin.schoolNamePlaceholder': 'e.g. Riverside High',
  'admin.schoolCode': 'School code (login code)',
  'admin.schoolCodeRule': 'Lowercase letters, numbers, and single hyphens only, 3–40 characters.',
  'admin.schoolCodeHint':
    'This is what the school’s staff type into the first step of the sign-in page to identify their school. Share it with the school once created, the same way you’d share a teacher’s one-time password.',
  'admin.addSchool': '+ Add school',
  'admin.adding': 'Adding…',
  'admin.schoolAdded': (n: string) => `School ${n} added.`,
  'admin.allSchools': 'All schools',
  'admin.noSchools': 'No schools yet.',
  'admin.newAccount': 'New teacher or school-lead account',
  'admin.school': 'School',
  'admin.chooseSchool': 'Choose a school…',
  'admin.role': 'Role',
  'admin.roleLeadAgg': 'School lead (aggregate view)',
  'admin.displayName': 'Display name',
  'admin.displayNamePlaceholder': 'e.g. Ms. Example',
  'admin.email': 'Email',
  'admin.firstClass': 'First class label (optional)',
  'admin.firstClassPlaceholder': 'e.g. Period 3 — never a student name',
  'admin.create': '+ Create account',
  'admin.creating': 'Creating…',
  'admin.created': (email: string) =>
    `Account created for ${email}. One-time password — copy this now and send it to them yourself; it will not be shown again:`,
  'admin.createdNote':
    'Have them sign in at /teacher/login and change it as soon as possible — there is no self-service password reset in this pilot build.',
  'admin.opsError': (m: string) => `Could not reach admin-ops: ${m}`,
  'admin.unknownError': 'Unknown error creating account.',
  'admin.roster': 'Staff roster',
  'admin.filter': 'Filter roster by school',
  'admin.noRoster': 'No staff accounts yet.',
  'admin.rosterError': 'Couldn’t load the staff list:',
  'admin.colName': 'Name',
  'admin.colRole': 'Role',
  'admin.colSchool': 'School',
  'admin.colEmail': 'Email',

  // sign-in context and roles (v2 step 2)
  'ctx.errorTitle': 'We couldn’t load your account',
  'ctx.errorBody': 'Your sign-in worked, but your roles couldn’t be loaded. Try again in a minute; if it keeps happening, tell your Swipe contact.',
  'ctx.backToSignIn': 'Back to sign in',
  'ctx.allSchools': 'All schools',
  'ctx.testBadge': 'TEST',
  'ctx.switchRole': 'Switch role',
  'ctx.actingAs': 'Acting as',
  'login.noAccess': 'This account can’t sign in right now. It may have been deactivated or expired — ask your school admin.',
  'login.unavailable': 'Signed in, but your roles couldn’t be loaded. Try again in a minute.',
  'role.platformAdmin': 'Platform admin',
  'role.schoolAdmin': 'School admin',
  'role.headOfSchool': 'Head of school',
  'role.counsellor': 'Counsellor',
  'role.platformAdminDesc': 'Set up and support schools. No access to check-in results.',
  'role.schoolAdminDesc': 'Manage staff, classes and settings for your school. No access to check-in results.',
  'role.headOfSchoolDesc': 'See school-wide check-in patterns.',
  'role.counsellorDesc': 'See check-in patterns for the grades you support.',
  'role.teacherDesc': 'Run check-ins and see your own classes’ results.',
  'choose.title': 'Choose a role',
  'choose.intro': (name: string) => `Hi ${name}. You have more than one role. Pick the one you want to work in — you can switch any time.`,
  'choose.testNotice': 'This is a test account.',
  'choose.current': 'current',
  'choose.error': 'That role couldn’t be switched on. Try again, or sign out and back in.',
  'home.nav': 'Home',
  'home.title': (role: string) => `${role} home`,
  'home.whatTitle': 'What this role is for',
  'home.school': (school: string) => `School: ${school}`,
  'home.comingSoon': 'The screens for this role are being built and will appear here in an upcoming update.',
  'noRoles.title': 'No active role',
  'noRoles.body': 'You’re signed in, but this account has no active role right now. Ask your school admin or your Swipe contact.',
  'mfa.navLink': 'Two-factor sign-in',
  'mfa.verifyTitle': 'Enter your code',
  'mfa.verifyIntro': 'Open your authenticator app and enter the 6-digit code for Swipe.',
  'mfa.codeLabel': '6-digit code',
  'mfa.codeError': 'That code didn’t work. Check the time on your phone and try the newest code.',
  'mfa.checking': 'Checking…',
  'mfa.verify': 'Continue',
  'mfa.manageTitle': 'Two-factor sign-in',
  'mfa.manageIntro': 'Platform admins sign in with a password and a code from an authenticator app.',
  'mfa.registered': (n: number) => (n === 0 ? 'No authenticator set up yet.' : n === 1 ? '1 authenticator set up.' : `${n} authenticators set up.`),
  'mfa.addBackupHint': 'Add a second authenticator as a backup (for example your password manager), so losing your phone doesn’t lock you out.',
  'mfa.firstAdded': 'Authenticator added. Now add a backup one.',
  'mfa.backupAdded': 'Backup authenticator added.',
  'mfa.step1': 'Open an authenticator app (Microsoft Authenticator, Google Authenticator, 1Password, Bitwarden…).',
  'mfa.step2': 'Scan this QR code, or type in the key below.',
  'mfa.qrAlt': 'QR code for setting up Swipe in an authenticator app',
  'mfa.secretLabel': 'Key:',
  'mfa.step3': 'Enter the 6-digit code the app shows.',
  'mfa.confirm': 'Confirm',
  'mfa.setUp': 'Set up an authenticator',
  'mfa.addBackup': 'Add a backup authenticator',
  'mfa.back': 'Back to admin',

  // admin overview, dead-end fixes (v2 step 2, round 2)
  'notFound.staffLink': 'Staff sign-in',
  'ctx.retry': 'Try again',
  'form.cancel': 'Cancel',
  'mfa.requiredNotice': 'Two-factor sign-in is required for platform admins. Set up an authenticator to continue, or sign out.',
  'mfa.lostDevice': 'Lost your phone? Use your backup authenticator. If you have none, the other platform admin can reset it for you.',
  'mfa.continue': 'Done — go to admin',
  'admin.navOverview': 'Overview',
  'overview.title': 'Overview',
  'overview.hello': (name: string) => `Welcome back, ${name}. Here’s how the platform looks today.`,
  'overview.schoolsSub': (s: number, c: number) => `${s} sandbox · ${c} customer`,
  'overview.attention': 'Needs attention',
  'overview.attentionSub': 'items to follow up',
  'overview.quick': 'Quick actions',
  'overview.addSchool': 'Add a school',
  'overview.addSchoolDesc': 'Create a school and get its login code.',
  'overview.addStaff': 'Add a staff account',
  'overview.addStaffDesc': 'Create a teacher or head-of-school account.',
  'overview.viewStaff': 'See all staff',
  'overview.viewStaffDesc': 'Everyone with an account, by school.',
  'overview.allClear': 'All clear',
  'overview.allClearBody': 'Nothing needs your attention right now.',
  'overview.schools': 'Schools',
  'overview.seeAllSchools': 'See all schools',
  'overview.attnMfa': 'Your two-factor sign-in isn’t set up yet.',
  'overview.attnMfaAction': 'Set up',
  'overview.attnMfaBackup': 'You have no backup authenticator.',
  'overview.attnMfaBackupAction': 'Add backup',
  'overview.attnPaperwork': (s: string) => `${s}: sandbox paperwork not signed.`,
  'overview.attnView': 'View',
  'overview.attnNoStaff': (s: string) => `${s}: no staff accounts yet.`,
  'overview.attnAddStaff': 'Add staff',
  'school.sandbox': 'Sandbox',
  'school.customer': 'Customer',
  'school.settingUp': 'Setting up',
  'school.live': 'Live',
  'school.suspended': 'Suspended',
  'school.offboarded': 'Offboarded',
  'school.paperworkSigned': 'Paperwork signed',
  'school.paperworkSent': 'Paperwork sent',
  'school.paperworkNone': 'Paperwork missing',
  'schools.colCode': 'Code',
  'schools.colStatus': 'Status',
  'schools.colStaff': 'Staff',
  'schools.colActions': 'Actions',
  'schools.staffCount': (n: number) => (n === 1 ? '1 staff account' : `${n} staff accounts`),
  'schools.addStaffFor': (s: string) => `Add staff for ${s}`,
  'addSchool.details': 'School details',
  'addSchool.codeIs': 'Login code:',
  'addSchool.nextStaff': 'Add staff for this school',
  'addSchool.another': 'Add another school',
  'addSchool.backToList': 'Back to schools',
  'addStaff.done': 'Account created',
  'addStaff.another': 'Add another account',
  'addStaff.backToList': 'Back to staff',
  'addStaff.noSchools': 'There are no schools yet. Add a school first, then its staff.',
  ...platformEn,
  ...headEn,
  ...teacherEn,
  ...r2En,
  ...r3En,
  ...r4En,
  ...r4bEn,
  ...r4cEn,
  ...r5En,
  ...r6En,
  ...r7En,
};

export type Strings = { [K in keyof typeof en]: (typeof en)[K] extends (...a: infer A) => string ? (...a: A) => string : string };

const fi: Strings = {
  'lang.label': 'Kieli',
  'skip': 'Siirry pääsisältöön',
  'loading': 'Ladataan…',
  'signOut': 'Kirjaudu ulos',
  'a11y.link': 'Saavutettavuusseloste',
  'week': (week, year) => `Viikko ${week} · ${year}`,
  'outOf5': 'viidestä',
  'notFound.title': 'Sivua ei löytynyt',
  'notFound.body': 'Tarkista linkki tai pyydä opettajalta tämän päivän kyselyn linkki.',
  'role.teacher': 'Opettaja',
  'role.schoolLead': 'Koulun johto',
  'role.admin': 'Ylläpitäjä',
  'band.crit': 'Huomio',
  'band.warn': 'Seurattava',
  'band.good': 'Hyvä',

  'mood.1': 'Tosi vaikeaa',
  'mood.2': 'Ei kovin hyvin',
  'mood.3': 'Ihan ok',
  'mood.4': 'Hyvin',
  'mood.5': 'Tosi hyvin',
  'topic.schoolwork': 'Koulutyöt',
  'topic.friends': 'Kaverit',
  'topic.family': 'Perhe',
  'topic.sleep': 'Uni',
  'topic.health': 'Terveys',
  'topic.exams': 'Kokeet',
  'topic.other': 'Jokin muu',

  'ci.pageTitle': 'Kysely',
  'ci.title': 'Miten sinulla menee tänään?',
  'ci.anon': 'Nimetön – opettaja näkee vain koko luokan yhteenvedon.',
  'ci.scaleHint': '1 on heikoin, 5 on paras.',
  'ci.topicsTitle': 'Onko jokin mielessäsi?',
  'ci.topicsHint': 'Vapaaehtoinen – valitse niin monta kuin haluat.',
  'ci.done': 'Valmis',
  'ci.sending': 'Lähetetään…',
  'ci.pickFirst': 'Valitse kasvo 1–5 jatkaaksesi.',
  'ci.error': 'Lähetys ei onnistunut. Kysy opettajalta, onko kysely vielä auki.',
  'ci.closedTitle': 'Tämä kysely ei ole nyt auki',
  'ci.closedBody': 'Pyydä opettajalta tämän päivän linkki tai QR-koodi.',
  'ci.doneTitle': 'Kiitos – vastauksesi on tallennettu',
  'ci.doneBody': 'Muuta ei tarvitse tehdä. Voit sulkea tämän sivun.',
  'safety.label': 'Tukea saatavilla',
  'safety.message':
    'Jos haluat jutella jonkun kanssa, koulukuraattori, terveydenhoitaja tai kuka tahansa opettaja voi auttaa. Hätätilanteessa soita 112. Tämä palvelu näyttää opettajille yleiskuvan, ei yksittäisiä vastauksia, joten se ei voi lähettää ketään tarkistamaan, miten sinä voit.',

  'login.pageTitle': 'Henkilökunnan kirjautuminen',
  'login.title': 'Henkilökunnan kirjautuminen',
  'login.codeIntro': 'Aloita syöttämällä koulusi tunnus.',
  'login.codeLabel': 'Koulun tunnus',
  'login.codePlaceholder': 'esim. riverside-high',
  'login.continue': 'Jatka',
  'login.checking': 'Tarkistetaan…',
  'login.codeError': 'Tunnuksella ei löytynyt koulua. Tarkista tunnus koulusi ylläpitäjältä.',
  'login.codeHelp':
    'Etkö tiedä koulusi tunnusta? Kysy koulusi ylläpitäjältä – tunnus annettiin, kun koulu otettiin käyttöön.',
  'login.adminSkip': 'Kirjaudutko pilotin ylläpitäjänä? Ohita tämä vaihe.',
  'login.credIntro': 'Kirjaudu sinulle myönnetyllä tunnuksella.',
  'login.signingInTo': 'Koulu:',
  'login.notYourSchool': 'Väärä koulu?',
  'login.adminBanner': 'Pilotin ylläpitäjän kirjautuminen',
  'login.enterCodeInstead': 'Syötä koulun tunnus',
  'login.email': 'Sähköposti',
  'login.password': 'Salasana',
  'login.signIn': 'Kirjaudu',
  'login.signingIn': 'Kirjaudutaan…',
  'login.credError': 'Sähköpostiosoitetta tai salasanaa ei tunnistettu.',
  'login.wrongSchool': (s) =>
    `Tunnusta ei ole liitetty kouluun ${s}. Tarkista koulun tunnus tai kirjaudu koululle ${s} myönnetyllä tunnuksella.`,

  'teacher.title': 'Luokkasi',
  'stat.classes': 'Luokat',
  'stat.classesSub': 'joissa teet kyselyitä',
  'stat.open': 'Avoinna olevat kyselyt',
  'stat.openSub': 'kukin on auki 55 minuuttia',
  'addClass.title': 'Lisää luokka',
  'addClass.label': 'Luokan nimi',
  'addClass.placeholder': 'esim. 8A',
  'addClass.button': '+ Lisää luokka',
  'addClass.adding': 'Lisätään…',
  'addClass.error': 'Luokan lisääminen epäonnistui – yritä uudelleen.',
  'addClass.added': (l) => `Luokka ${l} lisätty.`,
  'teacher.empty': 'Ei vielä luokkia – lisää luokka yllä tai pyydä pilotin koordinaattoria lisäämään se.',
  'class.open': 'Kysely auki',
  'class.closed': 'Suljettu',
  'session.share': (time) => `Jaa tämä tunnus ja linkki luokalle. Auki klo ${time} asti.`,
  'session.codeLabel': 'Kyselyn tunnus',
  'session.opened': (cls, code) => `Kysely avattu luokalle ${cls}. Tunnus ${code}.`,
  'results.intro': 'Tulokset näytetään vain koko luokan yhteenvetona, kun riittävän moni on vastannut.',
  'results.responses': (n) => `${n} vastausta`,
  'results.mood': 'Miten heillä menee',
  'results.topics': 'Mielessä',
  'results.hidden': 'piilotettu',
  'results.hiddenNote': '”piilotettu” = niin harva valitsi sen, ettei sitä voi näyttää turvallisesti.',
  'results.updated': (cls) => `Luokan ${cls} tulokset päivitetty.`,
  'results.average': 'Luokan keskiarvo',
  'sup.insufficientTitle': 'Liian vähän vastauksia näytettäväksi turvallisesti',
  'sup.insufficientClass': (n, f) => `Vastauksia ei ole vielä tarpeeksi (${n}/${f}) erittelyn näyttämiseen.`,
  'sup.unanimousTitle': 'Erittely piilotettu',
  'sup.unanimousClass': (n) =>
    `${n} vastausta, mutta lähes kaikki vastasivat samoin, mikä voisi paljastaa sen, joka vastasi toisin.`,
  'btn.startCheckin': 'Aloita kysely',
  'btn.load': 'Näytä tulokset',
  'btn.refresh': 'Päivitä tulokset',

  'school.title': 'Koko koulun tulokset',
  'school.nav': 'Koko koulu',
  'stat.responses': 'Vastaukset',
  'stat.responsesSub': 'kaikista mukana olevista luokista',
  'stat.avg': 'Koulun keskiarvo',
  'stat.notLoaded': 'ei vielä ladattu',
  'stat.insufficient': 'liian vähän tietoa',
  'stat.shown': 'Mitä näytetään',
  'stat.shownValue': 'Yhteenvedot',
  'stat.shownSub': 'koottu tieto, ei yksittäisiä vastauksia',
  'school.panel': 'Miten oppilailla menee',
  'school.intro':
    'Valitse ”Näytä tulokset”, niin kaikkien luokkien vastaukset yhdistetään – myös luokkien, jotka ovat liian pieniä näytettäviksi erikseen.',
  'school.refresh': 'Päivitä',
  'school.updated': 'Koko koulun tulokset päivitetty.',
  'sup.insufficientSchool': (n, f) => `Koko koulusta ei ole vielä tarpeeksi vastauksia (${n}/${f}).`,
  'sup.unanimousSchool': (n) =>
    `${n} vastausta, mutta erittely on piilotettu samasta syystä kuin lähes yksimielisen luokan erittely.`,

  'admin.title': 'Ylläpito',
  'admin.navSchools': 'Koulut',
  'admin.navAccounts': 'Tunnukset',
  'stat.schools': 'Koulut',
  'stat.schoolsSub': 'tällä alustalla',
  'stat.staff': 'Henkilökunnan tunnukset',
  'stat.staffSubFiltered': 'valitussa koulussa',
  'stat.staffSub': 'opettajat ja koulun johto',
  'stat.respVisible': 'Täällä näkyvät vastaukset',
  'stat.respVisibleValue': 'Ei mitään',
  'stat.respVisibleSub': 'ylläpito hallinnoi vain tunnuksia',
  'admin.schools': 'Koulut',
  'admin.schoolName': 'Koulun nimi',
  'admin.schoolNamePlaceholder': 'esim. Riverside High',
  'admin.schoolCode': 'Koulun tunnus (kirjautumistunnus)',
  'admin.schoolCodeRule': 'Vain pieniä kirjaimia, numeroita ja yksittäisiä yhdysmerkkejä, 3–40 merkkiä.',
  'admin.schoolCodeHint':
    'Koulun henkilökunta syöttää tämän tunnuksen kirjautumisen ensimmäisessä vaiheessa. Anna tunnus koululle, kun se on luotu, samaan tapaan kuin opettajan kertakäyttöinen salasana.',
  'admin.addSchool': '+ Lisää koulu',
  'admin.adding': 'Lisätään…',
  'admin.schoolAdded': (n) => `Koulu ${n} lisätty.`,
  'admin.allSchools': 'Kaikki koulut',
  'admin.noSchools': 'Ei vielä kouluja.',
  'admin.newAccount': 'Uusi opettajan tai koulun johdon tunnus',
  'admin.school': 'Koulu',
  'admin.chooseSchool': 'Valitse koulu…',
  'admin.role': 'Rooli',
  'admin.roleLeadAgg': 'Koulun johto (koostenäkymä)',
  'admin.displayName': 'Näyttönimi',
  'admin.displayNamePlaceholder': 'esim. Maija Esimerkki',
  'admin.email': 'Sähköposti',
  'admin.firstClass': 'Ensimmäisen luokan nimi (vapaaehtoinen)',
  'admin.firstClassPlaceholder': 'esim. 8A – ei koskaan oppilaan nimeä',
  'admin.create': '+ Luo tunnus',
  'admin.creating': 'Luodaan…',
  'admin.created': (email) =>
    `Tunnus luotu: ${email}. Kertakäyttöinen salasana – kopioi se nyt ja lähetä se itse; sitä ei näytetä uudelleen:`,
  'admin.createdNote':
    'Pyydä käyttäjää kirjautumaan osoitteessa /teacher/login ja vaihtamaan salasana mahdollisimman pian – tässä pilottiversiossa ei ole omatoimista salasanan palautusta.',
  'admin.opsError': (m) => `Yhteys admin-ops-palveluun epäonnistui: ${m}`,
  'admin.unknownError': 'Tunnuksen luonnissa tapahtui tuntematon virhe.',
  'admin.roster': 'Henkilökuntalista',
  'admin.filter': 'Suodata lista koulun mukaan',
  'admin.noRoster': 'Ei vielä henkilökunnan tunnuksia.',
  'admin.rosterError': 'Henkilökuntalistaa ei voitu ladata:',
  'admin.colName': 'Nimi',
  'admin.colRole': 'Rooli',
  'admin.colSchool': 'Koulu',
  'admin.colEmail': 'Sähköposti',

  // sign-in context and roles (v2 step 2)
  'ctx.errorTitle': 'Tilisi tietoja ei voitu ladata',
  'ctx.errorBody': 'Kirjautuminen onnistui, mutta roolejasi ei voitu ladata. Yritä hetken päästä uudelleen. Jos ongelma jatkuu, ota yhteyttä Swipe-yhteyshenkilöösi.',
  'ctx.backToSignIn': 'Takaisin kirjautumiseen',
  'ctx.allSchools': 'Kaikki koulut',
  'ctx.testBadge': 'TESTI',
  'ctx.switchRole': 'Vaihda roolia',
  'ctx.actingAs': 'Roolina',
  'login.noAccess': 'Tällä tilillä ei voi kirjautua juuri nyt. Tili on ehkä poistettu käytöstä tai vanhentunut – kysy koulusi ylläpitäjältä.',
  'login.unavailable': 'Kirjautuminen onnistui, mutta roolejasi ei voitu ladata. Yritä hetken päästä uudelleen.',
  'role.platformAdmin': 'Alustan ylläpitäjä',
  'role.schoolAdmin': 'Koulun ylläpitäjä',
  'role.headOfSchool': 'Rehtori',
  'role.counsellor': 'Kuraattori',
  'role.platformAdminDesc': 'Koulujen käyttöönotto ja tuki. Ei pääsyä tuloksiin.',
  'role.schoolAdminDesc': 'Koulun henkilökunnan, luokkien ja asetusten hallinta. Ei pääsyä tuloksiin.',
  'role.headOfSchoolDesc': 'Koko koulun vastausten yhteenvedot.',
  'role.counsellorDesc': 'Tukemiesi luokka-asteiden vastausten yhteenvedot.',
  'role.teacherDesc': 'Kyselyjen avaaminen ja omien luokkien tulokset.',
  'choose.title': 'Valitse rooli',
  'choose.intro': (name: string) => `Hei ${name}. Sinulla on useampi rooli. Valitse, missä roolissa haluat toimia – voit vaihtaa milloin tahansa.`,
  'choose.testNotice': 'Tämä on testitili.',
  'choose.current': 'nykyinen',
  'choose.error': 'Roolia ei voitu ottaa käyttöön. Yritä uudelleen tai kirjaudu ulos ja takaisin sisään.',
  'home.nav': 'Etusivu',
  'home.title': (role: string) => `${role}: etusivu`,
  'home.whatTitle': 'Mihin tätä roolia käytetään',
  'home.school': (school: string) => `Koulu: ${school}`,
  'home.comingSoon': 'Tämän roolin näkymiä rakennetaan, ja ne tulevat tähän tulevassa päivityksessä.',
  'noRoles.title': 'Ei aktiivista roolia',
  'noRoles.body': 'Olet kirjautunut, mutta tilillä ei ole nyt aktiivista roolia. Kysy koulusi ylläpitäjältä tai Swipe-yhteyshenkilöltäsi.',
  'mfa.navLink': 'Kaksivaiheinen tunnistautuminen',
  'mfa.verifyTitle': 'Syötä koodi',
  'mfa.verifyIntro': 'Avaa todennussovellus ja syötä Swipen 6-numeroinen koodi.',
  'mfa.codeLabel': '6-numeroinen koodi',
  'mfa.codeError': 'Koodi ei kelvannut. Tarkista puhelimen kellonaika ja kokeile uusinta koodia.',
  'mfa.checking': 'Tarkistetaan…',
  'mfa.verify': 'Jatka',
  'mfa.manageTitle': 'Kaksivaiheinen tunnistautuminen',
  'mfa.manageIntro': 'Alustan ylläpitäjät kirjautuvat salasanalla ja todennussovelluksen koodilla.',
  'mfa.registered': (n: number) => (n === 0 ? 'Todennussovellusta ei ole vielä otettu käyttöön.' : n === 1 ? '1 todennussovellus käytössä.' : `${n} todennussovellusta käytössä.`),
  'mfa.addBackupHint': 'Lisää toinen todennussovellus varalle (esimerkiksi salasanojen hallintaohjelma), jotta puhelimen katoaminen ei estä kirjautumista.',
  'mfa.firstAdded': 'Todennussovellus lisätty. Lisää nyt varasovellus.',
  'mfa.backupAdded': 'Varatodennussovellus lisätty.',
  'mfa.step1': 'Avaa todennussovellus (Microsoft Authenticator, Google Authenticator, 1Password, Bitwarden…).',
  'mfa.step2': 'Skannaa QR-koodi tai kirjoita alla oleva avain.',
  'mfa.qrAlt': 'QR-koodi Swipen lisäämiseksi todennussovellukseen',
  'mfa.secretLabel': 'Avain:',
  'mfa.step3': 'Syötä sovelluksen näyttämä 6-numeroinen koodi.',
  'mfa.confirm': 'Vahvista',
  'mfa.setUp': 'Ota todennussovellus käyttöön',
  'mfa.addBackup': 'Lisää varatodennussovellus',
  'mfa.back': 'Takaisin ylläpitoon',

  // admin overview, dead-end fixes (v2 step 2, round 2)
  'notFound.staffLink': 'Henkilökunnan kirjautuminen',
  'ctx.retry': 'Yritä uudelleen',
  'form.cancel': 'Peruuta',
  'mfa.requiredNotice': 'Alustan ylläpitäjiltä vaaditaan kaksivaiheinen tunnistautuminen. Ota todennussovellus käyttöön jatkaaksesi tai kirjaudu ulos.',
  'mfa.lostDevice': 'Puhelin kadonnut? Käytä varatodennussovellusta. Jos sinulla ei ole sellaista, toinen alustan ylläpitäjä voi nollata sen.',
  'mfa.continue': 'Valmis – siirry ylläpitoon',
  'admin.navOverview': 'Yleiskuva',
  'overview.title': 'Yleiskuva',
  'overview.hello': (name: string) => `Tervetuloa takaisin, ${name}. Tältä alusta näyttää tänään.`,
  'overview.schoolsSub': (s: number, c: number) => `${s} hiekkalaatikko · ${c} asiakas`,
  'overview.attention': 'Vaatii huomiota',
  'overview.attentionSub': 'seurattavaa asiaa',
  'overview.quick': 'Pikatoiminnot',
  'overview.addSchool': 'Lisää koulu',
  'overview.addSchoolDesc': 'Luo koulu ja saa sen kirjautumistunnus.',
  'overview.addStaff': 'Lisää henkilökunnan tili',
  'overview.addStaffDesc': 'Luo opettajan tai rehtorin tili.',
  'overview.viewStaff': 'Näytä koko henkilökunta',
  'overview.viewStaffDesc': 'Kaikki tilit kouluittain.',
  'overview.allClear': 'Kaikki kunnossa',
  'overview.allClearBody': 'Mikään ei vaadi huomiotasi juuri nyt.',
  'overview.schools': 'Koulut',
  'overview.seeAllSchools': 'Näytä kaikki koulut',
  'overview.attnMfa': 'Kaksivaiheista tunnistautumistasi ei ole vielä otettu käyttöön.',
  'overview.attnMfaAction': 'Ota käyttöön',
  'overview.attnMfaBackup': 'Sinulla ei ole varatodennussovellusta.',
  'overview.attnMfaBackupAction': 'Lisää varasovellus',
  'overview.attnPaperwork': (s: string) => `${s}: hiekkalaatikon sopimusta ei ole allekirjoitettu.`,
  'overview.attnView': 'Näytä',
  'overview.attnNoStaff': (s: string) => `${s}: ei vielä henkilökunnan tilejä.`,
  'overview.attnAddStaff': 'Lisää henkilökuntaa',
  'school.sandbox': 'Hiekkalaatikko',
  'school.customer': 'Asiakas',
  'school.settingUp': 'Käyttöönotossa',
  'school.live': 'Käytössä',
  'school.suspended': 'Keskeytetty',
  'school.offboarded': 'Poistettu käytöstä',
  'school.paperworkSigned': 'Sopimus allekirjoitettu',
  'school.paperworkSent': 'Sopimus lähetetty',
  'school.paperworkNone': 'Sopimus puuttuu',
  'schools.colCode': 'Tunnus',
  'schools.colStatus': 'Tila',
  'schools.colStaff': 'Henkilökunta',
  'schools.colActions': 'Toiminnot',
  'schools.staffCount': (n: number) => (n === 1 ? '1 henkilökunnan tili' : `${n} henkilökunnan tiliä`),
  'schools.addStaffFor': (s: string) => `Lisää henkilökuntaa: ${s}`,
  'addSchool.details': 'Koulun tiedot',
  'addSchool.codeIs': 'Kirjautumistunnus:',
  'addSchool.nextStaff': 'Lisää tämän koulun henkilökuntaa',
  'addSchool.another': 'Lisää toinen koulu',
  'addSchool.backToList': 'Takaisin kouluihin',
  'addStaff.done': 'Tili luotu',
  'addStaff.another': 'Lisää toinen tili',
  'addStaff.backToList': 'Takaisin henkilökuntaan',
  'addStaff.noSchools': 'Kouluja ei ole vielä. Lisää ensin koulu ja sitten sen henkilökunta.',
  ...platformFi,
  ...headFi,
  ...teacherFi,
  ...r2Fi,
  ...r3Fi,
  ...r4Fi,
  ...r4bFi,
  ...r4cFi,
  ...r5Fi,
  ...r6Fi,
  ...r7Fi,
};

const sv: Strings = {
  'lang.label': 'Språk',
  'skip': 'Hoppa till huvudinnehållet',
  'loading': 'Laddar…',
  'signOut': 'Logga ut',
  'a11y.link': 'Tillgänglighetsutlåtande',
  'week': (week, year) => `Vecka ${week} · ${year}`,
  'outOf5': 'av 5',
  'notFound.title': 'Sidan hittades inte',
  'notFound.body': 'Kontrollera länken eller be din lärare om dagens länk.',
  'role.teacher': 'Lärare',
  'role.schoolLead': 'Skolledning',
  'role.admin': 'Administratör',
  'band.crit': 'Uppmärksamhet',
  'band.warn': 'Bevaka',
  'band.good': 'Bra',

  'mood.1': 'Mycket tufft',
  'mood.2': 'Inte så bra',
  'mood.3': 'Okej',
  'mood.4': 'Bra',
  'mood.5': 'Jättebra',
  'topic.schoolwork': 'Skolarbete',
  'topic.friends': 'Kompisar',
  'topic.family': 'Familj',
  'topic.sleep': 'Sömn',
  'topic.health': 'Hälsa',
  'topic.exams': 'Prov',
  'topic.other': 'Något annat',

  'ci.pageTitle': 'Incheckning',
  'ci.title': 'Hur mår du i dag?',
  'ci.anon': 'Anonymt – läraren ser bara hela klassens sammanställning.',
  'ci.scaleHint': '1 är lägst, 5 är högst.',
  'ci.topicsTitle': 'Är det något du tänker på?',
  'ci.topicsHint': 'Frivilligt – välj så många du vill.',
  'ci.done': 'Klar',
  'ci.sending': 'Skickar…',
  'ci.pickFirst': 'Välj ett ansikte 1–5 för att fortsätta.',
  'ci.error': 'Det gick inte att skicka. Fråga din lärare om incheckningen fortfarande är öppen.',
  'ci.closedTitle': 'Den här incheckningen är inte öppen just nu',
  'ci.closedBody': 'Be din lärare om dagens länk eller QR-kod.',
  'ci.doneTitle': 'Tack – du är incheckad',
  'ci.doneBody': 'Du behöver inte göra något mer. Du kan stänga sidan.',
  'safety.label': 'Stöd',
  'safety.message':
    'Om du vill prata med någon kan skolkuratorn, skolhälsovårdaren eller vilken lärare som helst hjälpa dig. I nödsituationer, ring 112. Tjänsten visar lärarna en helhetsbild, inte enskilda svar, så den kan inte skicka någon för att kolla hur du mår.',

  'login.pageTitle': 'Inloggning för personal',
  'login.title': 'Inloggning för personal',
  'login.codeIntro': 'Börja med att ange din skolas kod.',
  'login.codeLabel': 'Skolans kod',
  'login.codePlaceholder': 't.ex. riverside-high',
  'login.continue': 'Fortsätt',
  'login.checking': 'Kontrollerar…',
  'login.codeError': 'Vi hittade ingen skola med den koden. Kontrollera den med skolans administratör.',
  'login.codeHelp':
    'Vet du inte skolans kod? Fråga skolans administratör – koden gavs när skolan lades till.',
  'login.adminSkip': 'Loggar du in som pilotens administratör? Hoppa över det här steget.',
  'login.credIntro': 'Logga in med kontot du har fått.',
  'login.signingInTo': 'Loggar in på',
  'login.notYourSchool': 'Fel skola?',
  'login.adminBanner': 'Inloggning för pilotens administratör',
  'login.enterCodeInstead': 'Ange en skolkod i stället',
  'login.email': 'E-post',
  'login.password': 'Lösenord',
  'login.signIn': 'Logga in',
  'login.signingIn': 'Loggar in…',
  'login.credError': 'E-postadressen eller lösenordet känns inte igen.',
  'login.wrongSchool': (s) =>
    `Kontot är inte kopplat till ${s}. Kontrollera skolkoden eller logga in med kontot som utfärdats för ${s}.`,

  'teacher.title': 'Dina klasser',
  'stat.classes': 'Klasser',
  'stat.classesSub': 'som du håller incheckningar för',
  'stat.open': 'Öppna incheckningar nu',
  'stat.openSub': 'var och en är öppen i 55 minuter',
  'addClass.title': 'Lägg till en klass',
  'addClass.label': 'Klassens namn',
  'addClass.placeholder': 't.ex. 8A',
  'addClass.button': '+ Lägg till klass',
  'addClass.adding': 'Lägger till…',
  'addClass.error': 'Det gick inte att lägga till klassen – försök igen.',
  'addClass.added': (l) => `Klassen ${l} har lagts till.`,
  'teacher.empty': 'Inga klasser ännu – lägg till en ovan eller be pilotens koordinator att lägga till en.',
  'class.open': 'Incheckning öppen',
  'class.closed': 'Stängd',
  'session.share': (time) => `Dela koden och länken med klassen. Öppen till kl. ${time}.`,
  'session.codeLabel': 'Incheckningskod',
  'session.opened': (cls, code) => `Incheckning öppnad för ${cls}. Kod ${code}.`,
  'results.intro': 'Resultaten visas bara som klassens sammanställning när tillräckligt många har svarat.',
  'results.responses': (n) => `${n} svar`,
  'results.mood': 'Hur de mår',
  'results.topics': 'Det de tänker på',
  'results.hidden': 'dold',
  'results.hiddenNote': '”dold” = för få valde det för att det ska kunna visas säkert.',
  'results.updated': (cls) => `Resultaten för ${cls} har uppdaterats.`,
  'results.average': 'Klassens medelvärde',
  'sup.insufficientTitle': 'För få svar för att visas säkert',
  'sup.insufficientClass': (n, f) => `Inte tillräckligt många svar ännu (${n} av ${f}) för att visa en fördelning.`,
  'sup.unanimousTitle': 'Fördelningen dold',
  'sup.unanimousClass': (n) =>
    `${n} svar, men nästan alla svarade likadant, vilket kan avslöja den som svarade annorlunda.`,
  'btn.startCheckin': 'Starta incheckning',
  'btn.load': 'Visa resultat',
  'btn.refresh': 'Uppdatera resultat',

  'school.title': 'Hela skolans resultat',
  'school.nav': 'Hela skolan',
  'stat.responses': 'Svar',
  'stat.responsesSub': 'från alla deltagande klasser',
  'stat.avg': 'Skolans medelvärde',
  'stat.notLoaded': 'inte laddat ännu',
  'stat.insufficient': 'för lite data',
  'stat.shown': 'Vad som visas',
  'stat.shownValue': 'Summor',
  'stat.shownSub': 'sammanställt, aldrig enskilda svar',
  'school.panel': 'Hur eleverna mår',
  'school.intro':
    'Välj ”Visa resultat” för att slå ihop alla klassers svar, även klasser som är för små för att visas separat.',
  'school.refresh': 'Uppdatera',
  'school.updated': 'Hela skolans resultat har uppdaterats.',
  'sup.insufficientSchool': (n, f) => `Det finns ännu inte tillräckligt många svar från skolan (${n} av ${f}).`,
  'sup.unanimousSchool': (n) =>
    `${n} svar, men fördelningen döljs av samma skäl som för en nästan enig klass.`,

  'admin.title': 'Administration',
  'admin.navSchools': 'Skolor',
  'admin.navAccounts': 'Konton',
  'stat.schools': 'Skolor',
  'stat.schoolsSub': 'på plattformen',
  'stat.staff': 'Personalkonton',
  'stat.staffSubFiltered': 'i den valda skolan',
  'stat.staffSub': 'lärare och skolledning',
  'stat.respVisible': 'Svar som syns här',
  'stat.respVisibleValue': 'Inga',
  'stat.respVisibleSub': 'administratören hanterar bara konton',
  'admin.schools': 'Skolor',
  'admin.schoolName': 'Skolans namn',
  'admin.schoolNamePlaceholder': 't.ex. Riverside High',
  'admin.schoolCode': 'Skolkod (inloggningskod)',
  'admin.schoolCodeRule': 'Endast små bokstäver, siffror och enstaka bindestreck, 3–40 tecken.',
  'admin.schoolCodeHint':
    'Skolans personal anger koden i inloggningens första steg för att välja sin skola. Ge koden till skolan när den har skapats, på samma sätt som en lärares engångslösenord.',
  'admin.addSchool': '+ Lägg till skola',
  'admin.adding': 'Lägger till…',
  'admin.schoolAdded': (n) => `Skolan ${n} har lagts till.`,
  'admin.allSchools': 'Alla skolor',
  'admin.noSchools': 'Inga skolor ännu.',
  'admin.newAccount': 'Nytt konto för lärare eller skolledning',
  'admin.school': 'Skola',
  'admin.chooseSchool': 'Välj en skola…',
  'admin.role': 'Roll',
  'admin.roleLeadAgg': 'Skolledning (sammanställd vy)',
  'admin.displayName': 'Visningsnamn',
  'admin.displayNamePlaceholder': 't.ex. Maria Exempel',
  'admin.email': 'E-post',
  'admin.firstClass': 'Första klassens namn (frivilligt)',
  'admin.firstClassPlaceholder': 't.ex. 8A – aldrig en elevs namn',
  'admin.create': '+ Skapa konto',
  'admin.creating': 'Skapar…',
  'admin.created': (email) =>
    `Konto skapat för ${email}. Engångslösenord – kopiera det nu och skicka det själv; det visas inte igen:`,
  'admin.createdNote':
    'Be personen logga in på /teacher/login och byta lösenordet så snart som möjligt – den här pilotversionen har ingen självbetjäning för lösenordsåterställning.',
  'admin.opsError': (m) => `Kunde inte nå admin-ops: ${m}`,
  'admin.unknownError': 'Okänt fel när kontot skapades.',
  'admin.roster': 'Personallista',
  'admin.filter': 'Filtrera listan efter skola',
  'admin.noRoster': 'Inga personalkonton ännu.',
  'admin.rosterError': 'Personallistan kunde inte laddas:',
  'admin.colName': 'Namn',
  'admin.colRole': 'Roll',
  'admin.colSchool': 'Skola',
  'admin.colEmail': 'E-post',

  // sign-in context and roles (v2 step 2)
  'ctx.errorTitle': 'Vi kunde inte läsa in ditt konto',
  'ctx.errorBody': 'Inloggningen lyckades, men dina roller kunde inte läsas in. Försök igen om en stund. Om det fortsätter, kontakta din Swipe-kontakt.',
  'ctx.backToSignIn': 'Tillbaka till inloggningen',
  'ctx.allSchools': 'Alla skolor',
  'ctx.testBadge': 'TEST',
  'ctx.switchRole': 'Byt roll',
  'ctx.actingAs': 'Roll',
  'login.noAccess': 'Det här kontot kan inte logga in just nu. Det kan ha inaktiverats eller gått ut – fråga skolans administratör.',
  'login.unavailable': 'Inloggningen lyckades, men dina roller kunde inte läsas in. Försök igen om en stund.',
  'role.platformAdmin': 'Plattformsadministratör',
  'role.schoolAdmin': 'Skoladministratör',
  'role.headOfSchool': 'Rektor',
  'role.counsellor': 'Kurator',
  'role.platformAdminDesc': 'Ta i bruk och stöd skolor. Ingen åtkomst till resultat.',
  'role.schoolAdminDesc': 'Hantera skolans personal, klasser och inställningar. Ingen åtkomst till resultat.',
  'role.headOfSchoolDesc': 'Se mönster för hela skolan.',
  'role.counsellorDesc': 'Se mönster för de årskurser du stöder.',
  'role.teacherDesc': 'Öppna avstämningar och se dina egna klassers resultat.',
  'choose.title': 'Välj roll',
  'choose.intro': (name: string) => `Hej ${name}. Du har flera roller. Välj den du vill arbeta i – du kan byta när som helst.`,
  'choose.testNotice': 'Det här är ett testkonto.',
  'choose.current': 'nuvarande',
  'choose.error': 'Rollen kunde inte aktiveras. Försök igen, eller logga ut och in igen.',
  'home.nav': 'Start',
  'home.title': (role: string) => `${role}: start`,
  'home.whatTitle': 'Vad rollen är till för',
  'home.school': (school: string) => `Skola: ${school}`,
  'home.comingSoon': 'Vyerna för den här rollen byggs och visas här i en kommande uppdatering.',
  'noRoles.title': 'Ingen aktiv roll',
  'noRoles.body': 'Du är inloggad, men kontot har ingen aktiv roll just nu. Fråga skolans administratör eller din Swipe-kontakt.',
  'mfa.navLink': 'Tvåstegsverifiering',
  'mfa.verifyTitle': 'Ange din kod',
  'mfa.verifyIntro': 'Öppna din autentiseringsapp och ange den sexsiffriga koden för Swipe.',
  'mfa.codeLabel': 'Sexsiffrig kod',
  'mfa.codeError': 'Koden fungerade inte. Kontrollera tiden i telefonen och prova den senaste koden.',
  'mfa.checking': 'Kontrollerar…',
  'mfa.verify': 'Fortsätt',
  'mfa.manageTitle': 'Tvåstegsverifiering',
  'mfa.manageIntro': 'Plattformsadministratörer loggar in med lösenord och en kod från en autentiseringsapp.',
  'mfa.registered': (n: number) => (n === 0 ? 'Ingen autentiseringsapp har ställts in ännu.' : n === 1 ? '1 autentiseringsapp inställd.' : `${n} autentiseringsappar inställda.`),
  'mfa.addBackupHint': 'Lägg till en andra autentiseringsapp som reserv (till exempel din lösenordshanterare), så att du inte blir utelåst om du tappar telefonen.',
  'mfa.firstAdded': 'Autentiseringsappen har lagts till. Lägg nu till en reserv.',
  'mfa.backupAdded': 'Reservappen har lagts till.',
  'mfa.step1': 'Öppna en autentiseringsapp (Microsoft Authenticator, Google Authenticator, 1Password, Bitwarden…).',
  'mfa.step2': 'Skanna QR-koden, eller skriv in nyckeln nedan.',
  'mfa.qrAlt': 'QR-kod för att lägga till Swipe i en autentiseringsapp',
  'mfa.secretLabel': 'Nyckel:',
  'mfa.step3': 'Ange den sexsiffriga koden som appen visar.',
  'mfa.confirm': 'Bekräfta',
  'mfa.setUp': 'Ställ in en autentiseringsapp',
  'mfa.addBackup': 'Lägg till en reservapp',
  'mfa.back': 'Tillbaka till administrationen',

  // admin overview, dead-end fixes (v2 step 2, round 2)
  'notFound.staffLink': 'Inloggning för personal',
  'ctx.retry': 'Försök igen',
  'form.cancel': 'Avbryt',
  'mfa.requiredNotice': 'Tvåstegsverifiering krävs för plattformsadministratörer. Ställ in en autentiseringsapp för att fortsätta, eller logga ut.',
  'mfa.lostDevice': 'Tappat telefonen? Använd din reservapp. Om du inte har någon kan den andra plattformsadministratören återställa den.',
  'mfa.continue': 'Klart – gå till administrationen',
  'admin.navOverview': 'Översikt',
  'overview.title': 'Översikt',
  'overview.hello': (name: string) => `Välkommen tillbaka, ${name}. Så här ser plattformen ut i dag.`,
  'overview.schoolsSub': (s: number, c: number) => `${s} sandlåda · ${c} kund`,
  'overview.attention': 'Behöver åtgärdas',
  'overview.attentionSub': 'saker att följa upp',
  'overview.quick': 'Snabbåtgärder',
  'overview.addSchool': 'Lägg till en skola',
  'overview.addSchoolDesc': 'Skapa en skola och få dess inloggningskod.',
  'overview.addStaff': 'Lägg till ett personalkonto',
  'overview.addStaffDesc': 'Skapa ett konto för lärare eller rektor.',
  'overview.viewStaff': 'Visa all personal',
  'overview.viewStaffDesc': 'Alla konton, per skola.',
  'overview.allClear': 'Allt klart',
  'overview.allClearBody': 'Inget behöver din uppmärksamhet just nu.',
  'overview.schools': 'Skolor',
  'overview.seeAllSchools': 'Visa alla skolor',
  'overview.attnMfa': 'Din tvåstegsverifiering är inte inställd ännu.',
  'overview.attnMfaAction': 'Ställ in',
  'overview.attnMfaBackup': 'Du har ingen reservapp.',
  'overview.attnMfaBackupAction': 'Lägg till reserv',
  'overview.attnPaperwork': (s: string) => `${s}: sandlådeavtalet är inte undertecknat.`,
  'overview.attnView': 'Visa',
  'overview.attnNoStaff': (s: string) => `${s}: inga personalkonton ännu.`,
  'overview.attnAddStaff': 'Lägg till personal',
  'school.sandbox': 'Sandlåda',
  'school.customer': 'Kund',
  'school.settingUp': 'Tas i bruk',
  'school.live': 'I bruk',
  'school.suspended': 'Pausad',
  'school.offboarded': 'Avslutad',
  'school.paperworkSigned': 'Avtal undertecknat',
  'school.paperworkSent': 'Avtal skickat',
  'school.paperworkNone': 'Avtal saknas',
  'schools.colCode': 'Kod',
  'schools.colStatus': 'Status',
  'schools.colStaff': 'Personal',
  'schools.colActions': 'Åtgärder',
  'schools.staffCount': (n: number) => (n === 1 ? '1 personalkonto' : `${n} personalkonton`),
  'schools.addStaffFor': (s: string) => `Lägg till personal för ${s}`,
  'addSchool.details': 'Skolans uppgifter',
  'addSchool.codeIs': 'Inloggningskod:',
  'addSchool.nextStaff': 'Lägg till personal för skolan',
  'addSchool.another': 'Lägg till en skola till',
  'addSchool.backToList': 'Tillbaka till skolorna',
  'addStaff.done': 'Kontot har skapats',
  'addStaff.another': 'Lägg till ett konto till',
  'addStaff.backToList': 'Tillbaka till personalen',
  'addStaff.noSchools': 'Det finns inga skolor ännu. Lägg först till en skola och sedan dess personal.',
  ...platformSv,
  ...headSv,
  ...teacherSv,
  ...r2Sv,
  ...r3Sv,
  ...r4Sv,
  ...r4bSv,
  ...r4cSv,
  ...r5Sv,
  ...r6Sv,
  ...r7Sv,
};

export const STRINGS = { en, fi, sv } as const;
export type Lang = keyof typeof STRINGS;
export const LANGS: { code: Lang; name: string }[] = [
  { code: 'en', name: 'English' },
  { code: 'fi', name: 'Suomi' },
  { code: 'sv', name: 'Svenska' },
];
